import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import styles from "./AddTrackerToCompare.module.scss";
import { useAppDispatch } from "src/store";
import { useGlobalPreloader, useModal } from "src/hooks";
import { updateTrackersCollection } from "src/store/actions";
import { CreateNewTrackersCollectionModal } from "src/features";
import { showToastNotification, triggerGtmEvent } from "src/utils";
import { useGlobalSearchLoader } from "src/containers/Home/hooks/mainAppDataLoaders";

// Inner imports
import { useUsedTrackers } from "./hooks";
import { DashboardInfo, TrackersSection } from "./components";

type Props = {
  submitHandler: () => void;
  trackersCollectionId: TrackersCollection.Data["id"];
};

export const AddTrackerToCompare: FC<Props> = ({
  submitHandler,
  trackersCollectionId,
}) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const { showGlobalPreloader, hideGlobalPreloader } = useGlobalPreloader();

  const { setModal } = useModal();
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);

  const { loadAllData, hasLoadedAll: hasLoadedAllData } =
    useGlobalSearchLoader();

  const [
    usedTrackers,
    addUsedTracker,
    removeUsedTracker,
    isUsedTrackersChanged,
  ] = useUsedTrackers(trackersCollectionId);

  const usedTrackersId = useMemo<Tracker.Data["id"][]>(
    () => usedTrackers.map(({ id }) => id),
    [usedTrackers],
  );

  useEffect(() => {
    triggerGtmEvent("DashboardAddComparison", {
      dashboardId: trackersCollectionId,
    });
  }, [trackersCollectionId]);

  const onCreateNewCollectionClickHandler = () => {
    setModal(
      "create-new-trackers-collection",
      <CreateNewTrackersCollectionModal
        trackerIds={usedTrackersId}
        submitHandler={submitHandler}
      />,
    );
  };

  const handleLoadMore = async () => {
    setIsLoadingMore(true);

    try {
      await loadAllData();
    } catch (error) {
      console.error("Error loading all data:", error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  async function onUpdateClickHandler() {
    try {
      showGlobalPreloader();

      const changes = {
        trackerIds: usedTrackers.map(({ id }) => id),
      };

      await dispatch(
        updateTrackersCollection({ id: trackersCollectionId, changes }),
      ).unwrap();
    } catch (error) {
      console.error(error);

      showToastNotification({
        type: "error",
        text: t("common.error.server_error"),
      });
    } finally {
      hideGlobalPreloader();

      submitHandler();
    }
  }

  return (
    <div className={styles.wrapper}>
      <DashboardInfo
        usedTrackers={usedTrackers}
        removeUsedTracker={removeUsedTracker}
        trackersCollectionId={trackersCollectionId}
      />
      <TrackersSection
        usedTrackers={usedTrackers}
        addUsedTracker={addUsedTracker}
        removeUsedTracker={removeUsedTracker}
        trackersCollectionId={trackersCollectionId}
        onUpdateClickHandler={onUpdateClickHandler}
        isUsedTrackersChanged={isUsedTrackersChanged}
        onCreateNewCollectionClickHandler={onCreateNewCollectionClickHandler}
        hasLoadedAll={hasLoadedAllData}
        isLoadingMore={isLoadingMore}
        handleLoadMore={handleLoadMore}
      />
    </div>
  );
};
